import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PaymentGateway from '../components/PaymentGateway';
import api, { API_BASE_URL, createReservation } from '../services/api';

const SintraReservation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getParam = (legacyKey, modernKey, fallback = '') => {
    return searchParams.get(legacyKey) || searchParams.get(modernKey) || fallback;
  };

  const incomingRoom = location.state?.room || null;
  const incomingSearchData = location.state?.searchData || {};

  const checkIn = getParam('CheckIn', 'checkIn', incomingSearchData.checkIn || '');
  const checkOut = getParam('CheckOut', 'checkOut', incomingSearchData.checkOut || '');
  const adults = parseInt(getParam('Adults', 'adults', String(incomingSearchData.adults || 1)), 10) || 1;
  const children = parseInt(getParam('Children', 'children', String(incomingSearchData.children || 0)), 10) || 0;
  const noOfRooms = parseInt(getParam('NoOfRooms', 'rooms', String(incomingSearchData.rooms || incomingRoom?.quantity || 1)), 10) || 1;

  const [formData, setFormData] = useState({
    title: 'Mr.',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    arrival: '',
    coupon: '',
    specialMessage: '',
    paymentMethod: 'online-payment',
    agreed: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [voucherState, setVoucherState] = useState({ applied: false, loading: false, data: null, message: '' });
  const [paymentData, setPaymentData] = useState(null);
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);

  const roomData = useMemo(() => {
    if (!incomingRoom) {
      return {
        id: searchParams.get('roomId') || '',
        roomTypeId: searchParams.get('roomId') || 0,
        name: 'Executive Room',
        image: '/img/rooms/60.jpg',
        basePriceNightly: 21735,
        taxAmount: 0,
        totalWithTaxNightly: 21735,
        hotelId: null,
        quantity: noOfRooms,
        maxAdults: adults,
        maxChildren: children
      };
    }

    const nightlyBase = Number(incomingRoom.basePriceNightly ?? incomingRoom.discountedPrice ?? incomingRoom.price ?? 0) || 0;
    const nightlyTax = Number(incomingRoom.taxAmount ?? 0) || 0;
    const nightlyTotal = Number(incomingRoom.totalWithTaxNightly ?? incomingRoom.totalWithTax ?? (nightlyBase + nightlyTax)) || (nightlyBase + nightlyTax);

    return {
      ...incomingRoom,
      quantity: incomingRoom.quantity || noOfRooms,
      basePriceNightly: nightlyBase,
      taxAmount: nightlyTax,
      totalWithTaxNightly: nightlyTotal,
      image: incomingRoom.image || '/img/rooms/60.jpg',
      name: incomingRoom.name || incomingRoom.roomTypeName || 'Executive Room'
    };
  }, [incomingRoom, searchParams, noOfRooms, adults, children]);

  const dateInfo = useMemo(() => {
    const inDate = checkIn ? new Date(checkIn) : null;
    const outDate = checkOut ? new Date(checkOut) : null;
    const validIn = inDate && !Number.isNaN(inDate.getTime()) ? inDate : null;
    const validOut = outDate && !Number.isNaN(outDate.getTime()) ? outDate : null;
    const nights = validIn && validOut ? Math.max(1, Math.round((validOut - validIn) / (1000 * 60 * 60 * 24))) : 1;
    return { inDate: validIn, outDate: validOut, nights };
  }, [checkIn, checkOut]);

  const totals = useMemo(() => {
    const quantity = Number(roomData.quantity || noOfRooms || 1);
    const subtotal = Number(roomData.basePriceNightly || 0) * dateInfo.nights * quantity;
    const tax = Number(roomData.taxAmount || 0) * dateInfo.nights * quantity;
    const grossTotal = subtotal + tax;
    const voucher = voucherState.data;
    let discount = 0;

    if (voucher) {
      const minimumAmount = Number(voucher.minimumAmount ?? voucher.MinimumAmount ?? 0) || 0;
      const maximumDiscount = Number(voucher.maximumDiscount ?? voucher.MaximumDiscount ?? 0) || 0;
      const discountType = String(voucher.discountType ?? voucher.DiscountType ?? 'Fixed').toLowerCase();
      if (grossTotal >= minimumAmount) {
        if (discountType === 'percentage') {
          const percentage = Number(voucher.discountPercentage ?? voucher.DiscountPercentage ?? 0) || 0;
          discount = (grossTotal * percentage) / 100;
        } else {
          discount = Number(voucher.discountAmount ?? voucher.DiscountAmount ?? 0) || 0;
        }
        if (maximumDiscount > 0) {
          discount = Math.min(discount, maximumDiscount);
        }
      }
    }

    discount = Math.min(discount, grossTotal);
    const total = Math.max(0, grossTotal - discount);
    const advanceAmount = formData.paymentMethod === 'online-payment' ? total : Number((total * 0.25).toFixed(2));
    return { subtotal, tax, grossTotal, discount, total, advanceAmount, quantity };
  }, [roomData, noOfRooms, dateInfo.nights, voucherState.data, formData.paymentMethod]);

  const formatDateNumber = (date) => (date ? date.getDate() : '--');
  const formatMonthYear = (date) => (date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A');
  const formatDayName = (date) => (date ? date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase() : 'N/A');
  const compactFieldStyle = {
    height: '42px',
    fontSize: '13px',
    borderRadius: '0',
    border: '0',
    borderBottom: '1px solid #ececec',
    boxShadow: 'none',
    padding: '8px 0',
    color: '#6b7280',
    backgroundColor: 'transparent'
  };
  const paymentCardBaseStyle = {
    border: '1px solid #2f6f88',
    padding: '16px 16px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    minHeight: '88px'
  };
  const reservationMetricBoxStyle = {
    backgroundColor: '#151515',
    padding: '16px 14px 12px',
    textAlign: 'center',
    minHeight: '92px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleApplyVoucher = async () => {
    const code = (formData.coupon || '').trim();
    if (!code) {
      setVoucherState({ applied: false, loading: false, data: null, message: 'Enter a voucher code.' });
      return;
    }

    try {
      setVoucherState((prev) => ({ ...prev, loading: true, message: '' }));
      const response = await api.get('/Vouchers', { params: { page: 1, pageSize: 100, status: 'Active' } });
      const vouchers = Array.isArray(response?.data?.data) ? response.data.data : [];
      const matchedVoucher = vouchers.find((voucher) => String(voucher.voucherCode || '').trim().toLowerCase() === code.toLowerCase());

      if (!matchedVoucher) {
        setVoucherState({ applied: false, loading: false, data: null, message: 'Voucher not found or inactive.' });
        return;
      }

      const roomTypeId = Number(roomData.roomTypeId || roomData.id || 0);
      const voucherRoomTypeId = Number(matchedVoucher.roomTypeId ?? matchedVoucher.RoomTypeId ?? 0);
      if (voucherRoomTypeId && roomTypeId && voucherRoomTypeId !== roomTypeId) {
        setVoucherState({ applied: false, loading: false, data: null, message: 'This voucher is not valid for the selected room type.' });
        return;
      }

      setVoucherState({ applied: true, loading: false, data: matchedVoucher, message: 'Voucher applied successfully.' });
    } catch (voucherError) {
      setVoucherState({ applied: false, loading: false, data: null, message: voucherError?.response?.data?.message || 'Unable to apply voucher.' });
    }
  };

  const formatDateForApi = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreed) {
      setError('Please accept the Terms and Conditions and Privacy Policy.');
      return;
    }

    if (!roomData || !roomData.roomTypeId) {
      setError('Room data is missing. Please go back and select a room again.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const bookingRequest = {
        hotelId: roomData.hotelId || 1,
        roomTypeId: roomData.roomTypeId || roomData.id || null,
        selectedRooms: [
          {
            roomTypeId: roomData.roomTypeId || roomData.id || 0,
            roomTypeName: roomData.name || '',
            quantity: totals.quantity,
            basePrice: Number(roomData.basePriceNightly || 0),
            taxAmount: Number(roomData.taxAmount || 0),
            totalWithTax: Number(roomData.totalWithTaxNightly || 0),
            planId: roomData.selectedPlan?.id || roomData.selectedPlan?.Id || null,
            planName: roomData.selectedPlan?.name || roomData.selectedPlan?.Name || '',
            planSurcharge: Number(roomData.selectedPlanExtraAmount || 0)
          }
        ],
        checkInDate: formatDateForApi(checkIn),
        checkOutDate: formatDateForApi(checkOut),
        numberOfAdults: adults,
        numberOfChildren: children,
        specialRequests: formData.specialMessage || '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
        address: formData.address,
        city: 'Islamabad',
        country: 'Pakistan',
        postalCode: '',
        idType: 'CNIC',
        idNumber: '',
        title: formData.title,
        company: '',
        notes: formData.specialMessage || '',
        arrivalTime: formData.arrival || '',
        couponCode: formData.coupon || '',
        voucherId: voucherState.data?.id || null,
        voucherCode: voucherState.data?.voucherCode || formData.coupon || '',
        bookingSource: 'Website',
        numberOfRooms: totals.quantity,
        nights: dateInfo.nights,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === 'online-payment' ? 'Paid' : 'Partial',
        paymentAccount: formData.paymentMethod === 'online-payment' ? 'PayFast' : 'Pay at Hotel',
        ratePlanId: roomData.selectedPlan?.id ? String(roomData.selectedPlan.id) : '',
        source: 'Website',
        market: 'Online',
        reservationMadeBy: `${formData.firstName} ${formData.lastName}`.trim(),
        checkinNotes: formData.arrival ? `Arrival Time: ${formData.arrival}` : '',
        enteredBy: 'Website',
        subtotal: totals.subtotal,
        taxAmount: totals.tax,
        discountAmount: totals.discount,
        advanceAmount: totals.advanceAmount,
        totalAmount: totals.total
      };

      const bookingResponse = await createReservation(bookingRequest);

      if (formData.paymentMethod === 'online-payment') {
        const paymentResponse = await fetch(`${API_BASE_URL}/Payfast/ProcessPayment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            BasketId: bookingResponse.reservationNumber || `LH${Date.now()}`,
            Amount: Number(bookingResponse.totalPaid ?? bookingResponse.totalAmount ?? totals.total ?? 0),
            CustomerEmail: formData.email,
            CustomerName: `${formData.firstName} ${formData.lastName}`.trim()
          })
        });

        if (!paymentResponse.ok) {
          const responseText = await paymentResponse.text();
          throw new Error(responseText || 'Failed to initiate online payment.');
        }

        const payfastPayload = await paymentResponse.json();
        const normalizedPaymentData = {
          PaymentUrl: payfastPayload.PaymentUrl || payfastPayload.paymentUrl,
          PaymentData: payfastPayload.PaymentData || payfastPayload.paymentData,
          Token: payfastPayload.Token || payfastPayload.token,
          BasketId: payfastPayload.BasketId || payfastPayload.basketId
        };

        setPaymentData({
          ...normalizedPaymentData,
          bookingResponse,
          reservationData: {
            guestInfo: {
              title: formData.title,
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              address: formData.address,
              arrival: formData.arrival,
              coupon: formData.coupon,
              specialRequests: formData.specialMessage
            },
            selectedRooms: [{ ...roomData, quantity: totals.quantity }],
            checkIn,
            checkOut,
            adults,
            children,
            totals,
            voucher: voucherState.data
          }
        });
        setShowPaymentGateway(true);
        return;
      }

      navigate('/thank-you', {
        state: {
          bookingResponse,
          reservationData: {
            guestInfo: {
              title: formData.title,
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              address: formData.address,
              arrival: formData.arrival,
              coupon: formData.coupon,
              specialRequests: formData.specialMessage
            },
            selectedRooms: [{ ...roomData, quantity: totals.quantity }],
            checkIn,
            checkOut,
            adults,
            children,
            totals,
            voucher: voucherState.data
          },
          paymentMethod: formData.paymentMethod
        }
      });
    } catch (submitError) {
      setError(submitError?.message || 'Failed to complete booking.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="banner-header section-padding valign bg-img" data-overlay-dark="4" data-background="/img/slider/sub.png" style={{ backgroundImage: 'url(/img/slider/sub.png)', backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12 caption mt-90 text-left">
              <span>
                <i className="star-rating"></i>
                <i className="star-rating"></i>
                <i className="star-rating"></i>
                <i className="star-rating"></i>
                <i className="star-rating"></i>
              </span>
              <h1>Book Luxury Hotel in Islamabad</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="section-padding">
        <div className="container">
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div className="col-lg-7 col-md-7">
              <h2 style={{ fontFamily: 'Gilda Display, serif', fontSize: '56px', lineHeight: '1.08em', marginBottom: '28px', color: '#0f172a' }}>Personal Information</h2>
              <form onSubmit={handleSubmit}>
                {error ? (
                  <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
                    {error}
                  </div>
                ) : null}
                <div className="row">
                  <div className="col-md-2">
                    <div className="form-group">
                      <select 
                        name="title" 
                        className="form-control"
                        value={formData.title}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                      >
                        <option value="Mr.">Mr.</option>
                        <option value="Mrs.">Mrs.</option>
                        <option value="Ms.">Ms.</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-group">
                      <input 
                        type="text" 
                        name="firstName"
                        className="form-control" 
                        placeholder="First Name *"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-group">
                      <input 
                        type="text" 
                        name="lastName"
                        className="form-control" 
                        placeholder="Last Name *"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <input 
                        type="email" 
                        name="email"
                        className="form-control" 
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input 
                        type="tel" 
                        name="phone"
                        className="form-control" 
                        placeholder="+92 3xxxxxxxxx"
                        value={formData.phone}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <input 
                        type="text" 
                        name="address"
                        className="form-control" 
                        placeholder="Address"
                        value={formData.address}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <input 
                        type="time" 
                        name="arrival"
                        className="form-control"
                        value={formData.arrival}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input 
                        type="text" 
                        name="coupon"
                        className="form-control" 
                        placeholder="Coupon"
                        value={formData.coupon}
                        onChange={handleInputChange}
                        style={compactFieldStyle}
                      />
                      <button type="button" className="btn btn-dark btn-sm" onClick={handleApplyVoucher} disabled={voucherState.loading} style={{ marginTop: '10px', minWidth: '102px', height: '28px', padding: '4px 14px', fontSize: '12px', borderRadius: '2px', lineHeight: '1.1' }}>{voucherState.loading ? 'Applying...' : 'Apply Voucher'}</button>
                      {voucherState.message ? (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: voucherState.applied ? '#15803d' : '#dc2626' }}>{voucherState.message}</div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <textarea 
                        name="specialMessage"
                        className="form-control" 
                        rows="3" 
                        placeholder="Special Message"
                        value={formData.specialMessage}
                        onChange={handleInputChange}
                        style={{ ...compactFieldStyle, height: '66px', resize: 'vertical', paddingTop: '10px' }}
                      ></textarea>
                    </div>
                  </div>
                </div>

                <h2 className="mt-4" style={{ fontFamily: 'Gilda Display, serif', fontSize: '56px', lineHeight: '1.08em', marginTop: '6px', marginBottom: '18px', color: '#0f172a' }}>Payment Method</h2>
                <div className="row">
                  <div className="col-md-6">
                    <div className="payment-option" style={{ 
                      ...paymentCardBaseStyle,
                      borderColor: formData.paymentMethod === 'online-payment' ? '#2f6f88' : '#2f6f88',
                      backgroundColor: '#ffffff',
                      position: 'relative'
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'online-payment' }))}
                    >
                      <input 
                        type="radio" 
                        name="paymentMethod" 
                        value="online-payment"
                        checked={formData.paymentMethod === 'online-payment'}
                        onChange={handleInputChange}
                      />
                      <label style={{ marginLeft: '10px', fontWeight: '400', fontSize: '15px', color: '#111827' }}>Online Payment</label>
                      {formData.paymentMethod === 'online-payment' ? (
                        <div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#2f6f88', color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          ✓
                        </div>
                      ) : null}
                      <div style={{ marginTop: '8px' }}>
                        <img src="/img/payment-icons.png" alt="Payment Methods" style={{ maxWidth: '118px', width: '100%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="payment-option" style={{ 
                      ...paymentCardBaseStyle,
                      borderColor: formData.paymentMethod === 'pay-at-hotel' ? '#2f6f88' : '#2f6f88',
                      backgroundColor: '#ffffff'
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'pay-at-hotel' }))}
                    >
                      <input 
                        type="radio" 
                        name="paymentMethod" 
                        value="pay-at-hotel"
                        checked={formData.paymentMethod === 'pay-at-hotel'}
                        onChange={handleInputChange}
                      />
                      <label style={{ marginLeft: '10px', fontWeight: '400', fontSize: '15px', color: '#111827' }}>25% Deposit</label>
                      <p style={{ marginTop: '7px', marginBottom: '0', fontSize: '12px', color: '#111827' }}>To confirm your booking.</p>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-md-12">
                    <div className="form-check" style={{ marginTop: '8px' }}>
                      <input type="checkbox" className="form-check-input" id="terms" name="agreed" checked={formData.agreed} onChange={handleInputChange} />
                      <label className="form-check-label" htmlFor="terms" style={{ fontSize: '13px', color: '#374151' }}>
                        I have read and agree to the <a href="/terms">Terms and Conditions</a> and <a href="/privacy">Privacy Policy</a>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-md-12">
                    <button type="submit" className="btn btn-primary btn-lg btn-block" style={{
                      backgroundColor: '#2f6f88',
                      border: 'none',
                      padding: '10px 14px',
                      fontSize: '12px',
                      fontWeight: '500',
                      letterSpacing: '0.22em',
                      minWidth: '116px',
                      borderRadius: '0',
                      lineHeight: '1.2'
                    }} disabled={submitting}>
                      {submitting ? 'PROCESSING...' : 'BOOK NOW'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="col-lg-5 col-md-5" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '2px', paddingRight: '8px' }}>
              <div className="card" style={{ 
                backgroundColor: '#1a1a1a', 
                color: 'white', 
                padding: '0',
                borderRadius: '0',
                maxWidth: '272px',
                width: '100%',
                border: '0'
              }}>
                <div style={{ position: 'relative' }}>
                  <img src={roomData.image} alt={roomData.name} style={{ width: '100%', height: '196px', objectFit: 'cover', borderRadius: '0' }} />
                  <div style={{ 
                    position: 'absolute', 
                    top: '10px', 
                    right: '10px', 
                    backgroundColor: '#1f1f1f',
                    padding: '4px 10px',
                    borderRadius: '2px',
                    fontSize: '11px',
                    lineHeight: '1.2'
                  }}>
                    {roomData.name}
                  </div>
                </div>

                <div style={{ padding: '14px 15px 15px' }}>
                  <h3 style={{ color: '#ffffff', textAlign: 'center', fontSize: '12px', letterSpacing: '0.18em', marginBottom: '12px' }}>YOUR RESERVATION</h3>

                  <div className="row" style={{ marginLeft: '-4px', marginRight: '-4px', rowGap: '8px' }}>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '10px', color: '#00bcd4', marginBottom: '8px' }}>CHECK-IN</div>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>{formatDateNumber(dateInfo.inDate)}</div>
                        <div style={{ fontSize: '10px', lineHeight: '1.45', color: '#ffffff' }}>{formatMonthYear(dateInfo.inDate)}<br />{formatDayName(dateInfo.inDate)}</div>
                      </div>
                    </div>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '10px', color: '#00bcd4', marginBottom: '8px' }}>CHECK-OUT</div>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>{formatDateNumber(dateInfo.outDate)}</div>
                        <div style={{ fontSize: '10px', lineHeight: '1.45', color: '#ffffff' }}>{formatMonthYear(dateInfo.outDate)}<br />{formatDayName(dateInfo.outDate)}</div>
                      </div>
                    </div>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>{dateInfo.nights}</div>
                        <div style={{ fontSize: '10px', color: '#00bcd4', letterSpacing: '0.08em' }}>NIGHTS</div>
                      </div>
                    </div>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>{adults + children}</div>
                        <div style={{ fontSize: '10px', color: '#00bcd4', letterSpacing: '0.08em' }}>GUEST</div>
                      </div>
                    </div>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>14:00</div>
                        <div style={{ fontSize: '10px', color: '#00bcd4', letterSpacing: '0.08em' }}>AFTER</div>
                      </div>
                    </div>
                    <div className="col-6" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                      <div style={reservationMetricBoxStyle}>
                        <div style={{ fontSize: '24px', lineHeight: '1', marginBottom: '6px' }}>12:00</div>
                        <div style={{ fontSize: '10px', color: '#00bcd4', letterSpacing: '0.08em' }}>BEFORE</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #262626', marginTop: '12px', paddingTop: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '17px', fontWeight: '400', color: '#ffffff', lineHeight: '1.2' }}>
                      {Math.round(totals.total)} <span style={{ fontSize: '13px' }}>PKR</span>
                    </div>
                    <div style={{ fontSize: '10px', color: '#00bcd4', marginTop: '3px' }}>TOTAL +TAX</div>
                    <div style={{ textAlign: 'center', marginTop: '7px', fontSize: '11px', color: '#d1d5db' }}>
                      {roomData.name} x {totals.quantity}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showPaymentGateway ? (
        <section className="section-padding" style={{ paddingTop: '0' }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card" style={{ padding: '30px', borderRadius: '8px' }}>
                  <PaymentGateway
                    paymentData={paymentData}
                    onError={(message) => setError(message || 'Unable to continue to payment gateway.')}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default SintraReservation;
